/**
 * Set a new password from a reset link.
 *
 * THE TOKEN IS THE WHOLE AUTHORISATION, so a missing one is not a form the person
 * should be allowed to fill in and then be told about. With no `?token=` the form
 * does not render at all: submitting it could only ever produce a `bad_token`,
 * and a screen that collects a password twice before admitting it cannot use it
 * is a screen that wasted the only attempt somebody was going to make.
 *
 * THREE 4xx CODES, THREE ANSWERS, AND TWO OF THEM ARE 400. `bad_token` and
 * `weak_password` share a status and mean opposite things — "this link is not
 * ours" versus "your password is fine as a link, weak as a password" — so the
 * branch is on `code` and never on `status`. `token_expired` is 410 and is the
 * common one: reset links are short-lived by design and most people who hit this
 * screen twice hit it after lunch. It gets a link straight back to Forgot
 * password, because the next action is to ask for another link and nothing else.
 *
 * WHAT THE SERVER SAYS ABOUT A WEAK PASSWORD IS SHOWN VERBATIM. The contract
 * promises the message names the rule that failed — the length, the email's local
 * part, the blocklist — and only the server knows which. Replacing it with
 * "password too weak" would drop the only useful half.
 *
 * THE TOKEN IS TAKEN OUT OF THE ADDRESS BAR ON ARRIVAL. It is a live credential
 * and the URL is the one place it can be read without touching the app —
 * `useLinkToken` captures it and scrubs the search string, which is why nothing
 * below reads the prop after the first render. The mechanics, and why the capture
 * has to come first, are on the hook.
 *
 * NO AUTOMATIC SIGN-IN ON SUCCESS. `POST /auth/reset-password` answers 204 and
 * sets no cookie, so there is no session to use; the screen offers a link to sign
 * in with the password just chosen, which also confirms it works. AcceptInvite is
 * the opposite case and behaves the other way, because that route does return a
 * session.
 */
import { useState } from 'react'
import { ApiError } from '@/api'
import { auth } from '@/lib/auth'
import { AuthShell } from '@/components/auth-shell'
import { Link, useLinkToken } from '@/components/app-sidebar'
import { PasswordField, passwordPairProblem } from '@/components/password-field'
import { Button } from '@/components/ui/button'

type Outcome =
  | { kind: 'form' }
  | { kind: 'done' }
  /** The link itself is unusable. There is no point re-submitting. */
  | { kind: 'dead'; expired: boolean; message: string }

export function ResetPasswordPage({ token: fromLink }: { token: string | null }) {
  // Captured on the first render and then gone from the URL. Everything below
  // reads this and never `fromLink`.
  const token = useLinkToken(fromLink)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'form' })

  const forgotLink = (
    <Link to="/forgot-password" className="underline underline-offset-4">
      Ask for a new reset link
    </Link>
  )

  if (token === null) {
    return (
      <AuthShell title="This link is incomplete" footer={forgotLink}>
        <p className="text-sm text-muted-foreground">
          A reset link carries a token in its address and this one has none, so there is
          nothing here to reset. It was probably truncated by an email client that broke the
          line.
        </p>
      </AuthShell>
    )
  }

  if (outcome.kind === 'done') {
    return (
      <AuthShell
        title="Password set"
        footer={
          <Link to="/login" className="underline underline-offset-4">
            Sign in
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground">
          Your new password is in place and the link you used has been spent. Sign in with it
          to confirm it works.
        </p>
      </AuthShell>
    )
  }

  if (outcome.kind === 'dead') {
    return (
      <AuthShell title={outcome.expired ? 'This link has expired' : 'This link is not valid'} footer={forgotLink}>
        <p className="text-sm text-muted-foreground">{outcome.message}</p>
      </AuthShell>
    )
  }

  const problem = passwordPairProblem(password, confirm)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || problem) return
    setBusy(true)
    setError(null)
    auth
      .resetPassword(token, password, AbortSignal.timeout(20_000))
      .then(() => setOutcome({ kind: 'done' }))
      .catch((err: unknown) => {
        setBusy(false)
        if (err instanceof ApiError && err.code === 'token_expired') {
          setOutcome({
            kind: 'dead',
            expired: true,
            message:
              'Reset links are deliberately short-lived, and this one has either run out, been used already or been revoked. Nothing has changed on your account.',
          })
          return
        }
        if (err instanceof ApiError && err.code === 'bad_token') {
          setOutcome({
            kind: 'dead',
            expired: false,
            message:
              'This panel does not recognise the token in that address. Check the link was copied whole, or ask for a fresh one.',
          })
          return
        }
        // weak_password lands here, and its message names the rule that failed.
        setError(err instanceof Error ? err.message : String(err))
      })
  }

  return (
    <AuthShell
      title="Choose a new password"
      intro="The link you followed is good. Pick the password you will sign in with from now on."
      footer={
        <Link to="/login" className="underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <PasswordField
          id="password"
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          showRule
          autoFocus
        />
        <PasswordField
          id="confirm"
          label="New password again"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />

        {error && <p className="err text-sm">{error}</p>}

        {/* The local problem is shown only once both boxes have something in
            them. "They do not match" against an empty second field is a
            complaint about a form somebody is still filling in. */}
        {!error && problem && password !== '' && confirm !== '' && (
          <p className="text-sm text-muted-foreground">{problem}</p>
        )}

        <Button type="submit" disabled={busy || problem !== null}>
          {busy ? 'Setting…' : 'Set the password'}
        </Button>
      </form>
    </AuthShell>
  )
}
